import { useEffect, useState } from "react";

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';

import { withTranslation } from "react-i18next";
import { useLocation, useNavigate } from 'react-router-dom';
import { isEqual } from "lodash";
import i18next from "@plannel/utils/i18n";

import authService from "@plannel/services/system/auth-service";
import systemService from "@plannel/services/system/system-service";
import ssoService from "@plannel/services/system/sso-service";

import { RouteList } from "@plannel/routeList";
import PlannelFooter from "@plannel/components/PlannelFooter";
import SelectLanguage from "@plannel/components/SelectLanguage"
import BackToSignIn from "@plannel/components/BackToSignIn";
import PasswordInput from "@plannel/components/PasswordInput";
import logo from "@plannel/assets/icons/main-logo.png";
import debug from "@plannel/utils/debug-util";
import CodeUtil from "../utils/code-util";

const { ID_PSWD } = CodeUtil.loginType;

const ChangePassword = ({ t }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const username = location?.state?.username;
  const password = location?.state?.password;
  const ssoType = location?.state?.ssoType;
  const tenant = location?.state?.tenant;

  const [showPassword, setShowPassword] = useState({ newPassword: false, confirmPassword: false });
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [msgObj, setMsgObj] = useState({ message: "" });

  const handleResponse = () => {
    if (location.pathname !== RouteList.Signin.path && location.pathname !== RouteList.ChangePassword.path && location.pathname !== RouteList.Root.path) {
      navigate(location.pathname);
    } else {
      navigate(RouteList.Home.path);
    }
    window.location.reload();
  }

  const handleErrorResponse = error => {
    const { data } = error.response;
    const errorMessage = data?.message ? data.message : "MSG_NetworkError";

    setMsgObj({
      ...msgObj,
      ...data,
      message: errorMessage
    });
  }

  const changePasswordExpiration = changePasswordFlg => {
    const params = { username, oldPassword, newPassword, confirmPassword, changePasswordFlg, resetPasswordFlg: false };

    systemService.changePassword(tenant, params)
      .then(
        () => {
          if (isEqual(ssoType, ID_PSWD)) {
            const loginPassword = changePasswordFlg ? newPassword : password;
            authService.login(tenant, username, loginPassword).then(
              () => handleResponse(),
              (error) => handleErrorResponse(error)
            );
          } else {
            const ssoParams = { email: username };
            ssoService.googleSignInSSO(tenant, ssoParams).then(
              () => handleResponse(),
              (error) => handleErrorResponse(error)
            );
          }
        },
        (error) => {
          const { message, details } = error.response.data;

          if (isEqual(message, "MSG_NotMatchCurrentPassword")) {
            setOldPassword("");
          }
          setNewPassword("");
          setConfirmPassword("");

          setMsgObj({ message, details });
          debug(error);
        }
      );
  }

  const setErrorMessage = () => {
    if (i18next.exists(msgObj.message)) {
      const messageWithCount = ["MSG_PasswordDCredit", "MSG_PasswordLCredit", "MSG_PasswordOCredit", "MSG_PasswordUCredit", "MSG_PasswordRepeat"];
      if (msgObj?.details?.count && messageWithCount.includes(msgObj.message)) {
        return t(msgObj.message, { N: msgObj.details.count });
      }
      if (msgObj?.details?.min && msgObj?.details?.max && isEqual(msgObj.message, "MSG_CheckPasswordLength")) {
        return t(msgObj.message, { min: msgObj.details.min, max: msgObj.details.max });
      }

      return t(msgObj.message);
    }

    return t("MSG_NetworkError");
  }

  useEffect(() => {
    if (!location?.state) {
      navigate(RouteList.Signin.path);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container id="app-home" component="main" maxWidth="xs">
      <CssBaseline />
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }} >
        <Box mt={8}>
          <img src={logo} width={170} alt="loading..." />
        </Box>
        <Typography component="h1" variant="h5" my={1}>{t("resetPassword")}</Typography>
        <Typography variant="body2">{t("MSG_PasswordExpired")}</Typography>
        <Typography variant="body2">{t("MSG_EnterNewPassword")}</Typography>
        <Box sx={{ mt: 1 }}>
          <PasswordInput
            name="oldPassword"
            label={t("currentPassword")}
            options={{ margin: "normal" }}
            password={oldPassword}
            setPassword={setOldPassword} />
          <PasswordInput
            name="newPassword"
            label={t("newPassword")}
            options={{ margin: "normal" }}
            password={newPassword}
            setPassword={setNewPassword}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            withVisibilityToggle />
          <PasswordInput
            name="confirmPassword"
            label={t("newPasswordConfirmation")}
            options={{ margin: "normal" }}
            password={confirmPassword}
            setPassword={setConfirmPassword}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            withVisibilityToggle />
          <Box sx={{ display: "flex", mt: 3, mb: 2 }}>
            <Button variant="contained" sx={{ width: "48%", mr: "2%" }} onClick={() => changePasswordExpiration(false)}>{t("tryLater")}</Button>
            <Button variant="contained" sx={{ width: "48%", ml: "2%" }} onClick={() => changePasswordExpiration(true)}>{t("confirm")}</Button>
          </Box>
          {Boolean(msgObj.message) && (
            <Alert sx={{ mb: 2 }} severity="error">{setErrorMessage()}</Alert>
          )}
          <Box sx={{ mt: 1, display: "flex" }}>
            <SelectLanguage />
            <BackToSignIn />
          </Box>
        </Box>
      </Box>
      <PlannelFooter/>
    </Container>
  );
};

export default withTranslation()(ChangePassword);